import React, { useEffect, Component } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './auth/context';
import ReusableErrorBoundary from './components/error-boundary/ErrorBoundary';
import HealthCheck from './components/health-check/health-check';
import Router from './router/router';
import './App.scss';
import 'react-toastify/dist/ReactToastify.css';
import useResponsiveStore from './store/responsive-store';
import monitoringService from './utils/monitoring/monitoring-service';
import './components/error-boundary/ErrorBoundary.scss';

// Error Boundary component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    // Log the error to our monitoring service
    monitoringService.trackError('errorBoundary', error, { errorInfo });
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong.</h2>
          <p>Please try refreshing the page or contact support if the problem persists.</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const { setResponsiveData } = useResponsiveStore();

  useEffect(() => {
    const handleResize = () => {
      setResponsiveData({
        isMobile: window.innerWidth < 768,
        isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
        isDesktop: window.innerWidth >= 1024,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    // Track application load event
    monitoringService.trackEvent('applicationLoaded', {
      timestamp: new Date().toISOString(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      // Clean up monitoring service resources
      monitoringService.cleanup();
    };
  }, [setResponsiveData]);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ToastContainer />
          <ReusableErrorBoundary context="Router" showReload={false}>
            <Router />
          </ReusableErrorBoundary>
          {
            <HealthCheck />
          }
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
